import { useFormikContext } from 'formik';
import moment from 'moment';
import React from 'react';
import { t } from 'Src/i18n';
import { momentToApiDate } from 'Utils/date/apiDate';
import { Button } from 'Components/frameworks/button/Button';
import type { FormProduct } from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import { proposalsQuery } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import { useAppContext } from 'Context/ReactAppContext';
import { hasFetchProposalsFailed } from 'Utils/proposal/proposalUtils';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
export type PrebookingFormSearchButtonProps = {
  product: FormProduct | null;
  setProposal: (proposal: any) => void;
  setIsLoading: (isLoading: boolean) => void;
  setFormStep: (formStep: number) => void;
  setFetchProposalError: (fetchProposalError: string) => void;
};
export const PrebookingFormSearchButton = ({
  product,
  setProposal,
  setIsLoading,
  setFormStep,
  setFetchProposalError,
}: PrebookingFormSearchButtonProps) => {
  const { values, setTouched } = useFormikContext();
  const appContext = useAppContext();
  const { trackClickEvent } = useTrackEvent();

  const computeFormValues = () => {
    const kidsKnownAttendeesInformation = values.kids.map((kid) => ({
      customerId: '',
      birthdate: kid.birthdate,
    }));
    const adultsKnownAttendeesInformation = values.adults.map(() => ({
      customerId: '',
      birthdate: '',
    }));
    const accommodationCategoryOptions = product ? product.accommodations : [];
    const accommodationIds = accommodationCategoryOptions
      .find((accommodationCategory) => accommodationCategory.value === values.accommodationCategory)
      ?.types.map((accommodation) => accommodation.value);
    const knownAttendeesInformation = [
      ...adultsKnownAttendeesInformation,
      ...kidsKnownAttendeesInformation,
    ];
    return {
      departureCity: values.departureCity,
      numberAttendees: Number(values.kidsNumber) + Number(values.adultsNumber) || 1,
      knownAttendeesInformation: knownAttendeesInformation,
      productId: values.productId,
      arrivalDate: momentToApiDate(moment(values.departureDate.from)),
      duration: values.duration,
      fetchActivityCategories: false,
      kids: values.kids.map((kid) => ({
        birthdate: kid.birthdate,
        childcareIncluded: false,
      })),
      adults: values.adults.map(() => ({
        birthdate: undefined,
        civility: undefined,
        firstName: undefined,
        lastName: undefined,
        neolid: undefined,
        unicodeFirstName: undefined,
        unicodeLastName: undefined,
      })),
      accommodationIds,
      isPrebookingFormQuery: true,
    };
  };

  async function fetchProposals() {
    const firstStepKeys = [
      'departureDate',
      'accommodationCategory',
      'adultsNumber',
      'kidsNumber',
      'departureCity',
      'numberAttendees',
      'knownAttendeesInformation',
      'productId',
      'arrivalDate',
      'duration',
    ];
    const errors = await setTouched(
      Object.keys(values).reduce((acc, key) => {
        if (key === 'kids') {
          acc[key] = values.kids.map(() => ({
            birthdate: true,
          }));
        } else if (firstStepKeys.includes(key)) {
          acc[key] = true;
        }

        return acc;
      }, {}),
    );
    const hasFirstPartErrors =
      errors &&
      Object.keys(errors).some(
        (key) =>
          firstStepKeys.includes(key) ||
          (key === 'kids' && errors.kids.some((kid) => !!kid.birthdate)),
      );

    if (!hasFirstPartErrors) {
      setIsLoading(true);
      trackClickEvent(TRACK_EVENT_NAMES.CLICK_PREBOOKING_FORM_PREREG, {
        detail_click: 'estimate_my_stay',
      });
      const searchProposalParams = computeFormValues();
      const { proposals } = await proposalsQuery(searchProposalParams, appContext);
      const isFetchProposalsFailed = hasFetchProposalsFailed(proposals);

      if (!isFetchProposalsFailed) {
        const [firstProposal] = proposals.proposals;
        setProposal(firstProposal);
      } else {
        const fetchProposalErrorCode = proposals?.errors?.[0]?.errorCode;

        if (fetchProposalErrorCode) {
          setFetchProposalError(fetchProposalErrorCode);
        }
      }

      setFormStep(1);
      setIsLoading(false);
    }
  }

  return (
    <Button data-testid="prebooking-form-proposal-search-submit" onClick={() => fetchProposals()}>
      {t('prebookingForm.estimativePrice.labelCTA')}
    </Button>
  );
};
